/**
 * ReportCard Component
 * Card de relatório com informações e ações
 */

import React from 'react';
import {
  Play,
  Settings,
  Users,
  FileCheck,
  TrendingUp,
  Calendar,
  Sparkles
} from 'lucide-react';
import type { ReportDefinition } from '@/services/reports-service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface ReportCardProps {
  report: ReportDefinition;
  stats?: {
    totalRecords?: number;
    avgExecutionTime?: number;
    lastExecuted?: Date;
    executions?: number;
  };
  isNew?: boolean;
  isExecuting?: boolean;
  onExecute: () => void;
  onConfigure?: () => void;
}

const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  clients: Users,
  cases: FileCheck,
  tasks: TrendingUp,
  finance: TrendingUp
};

const formatDate = (date: Date) =>
  new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);

const formatExecutionTime = (ms: number) => (ms < 1000 ? `${ms}ms` : `${Math.round(ms / 1000)}s`);

const StatItem: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="rounded-md bg-muted/50 p-2 text-center">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-sm font-semibold tabular-nums">{value}</p>
  </div>
);

export const ReportCard: React.FC<ReportCardProps> = ({
  report,
  stats,
  isNew = false,
  isExecuting = false,
  onExecute,
  onConfigure
}) => {
  const CategoryIcon = categoryIcons[report.category] ?? FileCheck;

  return (
    <Card className="min-w-0 transition-shadow hover:shadow-md">
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <div className="shrink-0 rounded-lg bg-muted p-2">
            <CategoryIcon className="size-5 text-muted-foreground" aria-hidden="true" />
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="break-words text-base font-semibold">{report.name}</h3>
            <p className="text-sm capitalize text-muted-foreground">
              {report.category} • {report.reportType}
            </p>
          </div>

          {(isNew || report.isPublic) && (
            <div className="flex shrink-0 flex-col items-end gap-1">
              {isNew && (
                <Badge>
                  <Sparkles />
                  NOVO
                </Badge>
              )}
              {report.isPublic && <Badge variant="secondary">Público</Badge>}
            </div>
          )}
        </div>

        <p className="line-clamp-2 min-h-10 text-sm text-muted-foreground">{report.description}</p>

        {stats && (
          <div className="grid grid-cols-3 gap-2">
            {stats.totalRecords !== undefined && <StatItem label="Registros" value={stats.totalRecords} />}
            {stats.avgExecutionTime !== undefined && (
              <StatItem label="Tempo" value={formatExecutionTime(stats.avgExecutionTime)} />
            )}
            {stats.executions !== undefined && <StatItem label="Execuções" value={stats.executions} />}
          </div>
        )}

        {stats?.lastExecuted && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar className="size-3" aria-hidden="true" />
            <span>Última execução: {formatDate(stats.lastExecuted)}</span>
          </div>
        )}

        {report.tags && report.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {report.tags.slice(0, 3).map(tag => (
              <Badge key={tag} variant="secondary">{tag}</Badge>
            ))}
          </div>
        )}
      </CardContent>

      <CardFooter className="gap-2">
        <Button onClick={onExecute} loading={isExecuting} className="flex-1">
          {isExecuting ? (
            'Executando...'
          ) : (
            <>
              <Play className="size-4" />
              Executar
            </>
          )}
        </Button>

        {onConfigure && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={onConfigure} aria-label="Configurar">
                <Settings className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Configurar</TooltipContent>
          </Tooltip>
        )}
      </CardFooter>
    </Card>
  );
};
